/**
 * I7-B copy (bulk status change, selected export and bulk SMS) in the three
 * operator languages.
 *
 * The catalog lives in its own bounded vue-i18n instance: the application
 * catalog (`core/plugins/i18n.ts`) is not extended, it only registers itself
 * here so I7-B follows the current application locale, resolved to
 * `ro | ru | en` (anything else → `ro`). Counts use `Intl.PluralRules`, so
 * Romanian and Russian forms are grammatical for 1, 2–4, 5–20, 21 and 200.
 */
import {computed, unref, type ComputedRef} from "vue";
import {createI18n} from "vue-i18n";
import {elementLocaleFor} from "@/i18n/i7a";

export const I7B_LOCALES = ["ro", "ru", "en"] as const;
export type I7bLocale = typeof I7B_LOCALES[number];
export const I7B_DEFAULT_LOCALE: I7bLocale = "ro";

type Catalog = {[key: string]: string | Catalog};

const ro: Catalog = {
  plural: {
    bookings: {
      one: "{count} rezervare",
      few: "{count} rezervări",
      many: "{count} de rezervări",
      other: "{count} de rezervări",
    },
    selected: {
      one: "Selectate: {count}",
      few: "Selectate: {count}",
      many: "Selectate: {count}",
      other: "Selectate: {count}",
    },
  },
  selection: {
    rowLabel: "Selectează rezervarea #{id}",
    pageLabel: "Selectează toate rezervările de pe această pagină",
    limit: "Puteți selecta cel mult {max} rezervări odată.",
    announce: "Rezervări selectate: {count}.",
    cleared: "Selecția a fost golită.",
  },
  toolbar: {
    region: "Acțiuni pentru rezervările selectate",
    ids: "ID: {ids}",
    idsMore: "+{count}",
    showIds: "Toate ID-urile selectate",
    status: "Schimbă statutul",
    export: "Exportă selectate",
    clear: "Golește selecția",
    statusAria: "Schimbă statutul pentru {bookings}",
    exportAria: "Exportă selectate: {bookings}, în XLSX",
    clearAria: "Golește selecția ({bookings})",
    sms: "Trimite SMS",
    smsAria: "Trimite SMS pentru {bookings}",
    smsBlocked: "Trimiterea SMS anterioară nu este confirmată. Reîncercați-o sau abandonați-o mai întâi.",
  },
  sms: {
    title: "Trimite SMS",
    reviewTitle: "Verificați SMS-ul",
    heading: "SMS pentru {bookings}",
    reviewHeading: "Verificați SMS-ul pentru {bookings}",
    ids: "ID-urile rezervărilor",
    intro: "Scrieți mesajul în limbile dorite. Fiecare rezervare primește mesajul în limba ei; dacă nu există mesaj în acea limbă, primește mesajul în limba de rezervă.",
    restored: "Am păstrat mesajul nefinalizat pentru aceste rezervări.",
    discard: "Șterge mesajul",
    discarded: "Mesajul a fost șters.",
    languagesLegend: "Limbile mesajului",
    lang: {
      ro: "Română",
      ru: "Rusă",
      en: "Engleză",
    },
    include: {
      ro: "Include mesaj în română",
      ru: "Include mesaj în rusă",
      en: "Include mesaj în engleză",
    },
    messageLabel: {
      ro: "Mesajul în română",
      ru: "Mesajul în rusă",
      en: "Mesajul în engleză",
    },
    hint: "Între 1 și {max} de caractere. Spațiile de la început și de la sfârșit nu se numără.",
    counter: "{count}/{max} caractere",
    errorEmpty: "Scrieți mesajul sau debifați această limbă.",
    errorTooLong: "Mesajul are {count} caractere. Maximum {max}.",
    errorNoLanguage: "Alegeți cel puțin o limbă și scrieți mesajul.",
    fallbackLegend: "Limba de rezervă",
    fallbackHelp: "Folosită când limba rezervării nu are un mesaj dedicat.",
    fallbackChanged: "Limba de rezervă este acum: {language}.",
    fallbackCleared: "Nu mai este inclusă nicio limbă. Alegeți cel puțin una.",
    review: "Verifică mesajul",
    back: "Înapoi la mesaj",
    cancel: "Renunță",
    confirm: "Confirmă și trimite",
    reviewLanguages: "Limbi incluse",
    reviewFallback: "Limba de rezervă",
    reviewNote: "După confirmare trimiterea nu mai poate fi oprită. Numărul de telefon și expeditorul sunt stabilite de server pentru fiecare rezervare.",
    closeTitle: "Închideți fereastra?",
    closeText: "Mesajul nu a fost trimis. Dacă închideți, el rămâne doar în această pagină, cât timp nu schimbați selecția, filtrele, compania sau contul.",
    closeStay: "Continuă editarea",
    closeKeep: "Închide și păstrează mesajul",
    closeDiscard: "Șterge mesajul și închide",
  },
  smsPending: {
    region: "Trimitere SMS neconfirmată",
    text: "Nu știm dacă ultima trimitere SMS a ajuns la server. Nu puteți trimite un SMS nou până nu o reîncercați sau o abandonați.",
    textNoRetry: "Nu știm dacă ultima trimitere SMS a ajuns la server, iar ea nu mai poate fi reîncercată de aici. Verificați istoricul rezervărilor, apoi abandonați-o pentru a scrie un mesaj nou.",
    open: "Vezi încercarea și reîncearcă",
    abandon: "Abandonează încercarea",
    abandonConfirm: "Abandonați încercarea? Nu se trimite și nu se anulează nimic: dacă SMS-ul a ajuns deja la server, rămâne trimis. Un mesaj nou va cere o verificare și o confirmare separată.",
    abandonKeep: "Nu, păstrează încercarea",
    abandonYes: "Da, abandonează",
    abandoned: "Încercarea a fost abandonată. Puteți scrie un mesaj nou.",
  },
  status: {
    title: "Schimbă statutul rezervărilor selectate",
    intro: "Statutul nou se aplică doar rezervărilor selectate. Cele care nu pot fi schimbate vor fi raportate separat.",
    targetLabel: "Statut nou",
    targetPlaceholder: "Alegeți statutul",
    targetRequired: "Alegeți statutul nou.",
    reasonLabel: "Motiv (opțional)",
    reasonHint: "Maximum {max} caractere.",
    reasonTooLong: "Motivul depășește {max} caractere.",
    refundNote: "Eventualele rambursări urmează politica rutei și nu pot fi modificate aici.",
    noStatuses: "Lista de statuturi nu este disponibilă. Reîncărcați pagina.",
    review: "Continuă",
    back: "Înapoi",
    cancel: "Renunță",
    confirmTitle: "Confirmați schimbarea",
    confirmText: "Statutul „{title}” (ID {value}) va fi setat pentru {bookings}.",
    confirmIds: "ID-urile rezervărilor",
    confirm: "Confirmă schimbarea",
    invalidated: "Selecția, contul sau permisiunile s-au schimbat. Schimbarea de statut nu a fost trimisă; selectați din nou rezervările.",
  },
  export: {
    confirmTitle: "Exportă rezervările selectate",
    confirmText: "Se va crea un fișier XLSX cu {bookings}. Exportul listei filtrate rămâne separat.",
    confirmIds: "ID-urile rezervărilor",
    confirm: "Creează exportul",
    cancel: "Renunță",
    download: "Descarcă XLSX",
    downloading: "Se descarcă…",
    downloaded: "Descărcarea a început.",
    expiresAt: "Fișierul este disponibil până la {date}.",
    partialFile: "Fișierul conține doar rezervările exportate cu succes. Mai jos sunt afișate rezultatele pe rezervări pe care le puteți vedea.",
    preparing: "Fișierul se pregătește…",
    noFile: "Nu există un fișier de descărcat pentru acest export.",
    notReady: "Fișierul nu este încă gata. Am actualizat starea.",
    expired: "Fișierul a expirat. Creați un export nou.",
    scopeChanged: "Accesul la unele rezervări s-a schimbat după crearea fișierului, așa că nu mai poate fi descărcat. Creați un export nou.",
    unavailable: "Fișierul nu mai este disponibil. Creați un export nou.",
    newExport: "Creează un export nou",
    newExportHint: "Exportul nou folosește rezervările selectate acum în listă.",
  },
  progress: {
    titleStatus: "Schimbare de statut în masă",
    titleExport: "Export rezervări selectate",
    titleSms: "Trimitere SMS",
    doneOkSms: "Trimiterea s-a încheiat. „Trimis” înseamnă că furnizorul a acceptat SMS-ul; livrarea este confirmată doar unde scrie „Livrat”.",
    doneWithErrorsSms: "Trimiterea s-a încheiat cu erori. Unele SMS-uri nu au fost trimise; vedeți detaliile mai jos.",
    doneUnknownSms: "Trimiterea s-a încheiat, dar pentru unele rezervări nu putem confirma rezultatul SMS-ului. Vedeți detaliile mai jos.",
    smsUnknown: "Pentru unele rezervări nu putem confirma dacă SMS-ul a fost trimis. Nu retrimiteți automat: verificați istoricul rezervării.",
    smsUncertainNote: "Închiderea ferestrei nu abandonează încercarea. Până nu o reîncercați sau o abandonați explicit, nu puteți trimite un SMS nou.",
    smsRetryRefused: "Reîncercarea a fost refuzată, dar prima trimitere SMS rămâne neconfirmată: este posibil să fi fost trimisă. Închideți fereastra și verificați istoricul rezervărilor înainte de a abandona încercarea sau de a trimite un mesaj nou.",
    submitting: "Se trimite cererea…",
    uncertain: "Nu știm sigur dacă cererea a ajuns la server. Reîncercarea este sigură: trimite aceeași cerere și nu dublează acțiunea.",
    retrySubmit: "Reîncearcă trimiterea",
    rejected: "Cererea nu a fost acceptată.",
    loading: "Se încarcă progresul…",
    processed: "Procesate {processed} din {total}",
    progressLabel: "Progresul operațiunii",
    polling: "Starea se actualizează automat.",
    runningNote: "Puteți închide fereastra. Operațiunea continuă pe server și o puteți redeschide din lista de rezervări.",
    retry: "Reîncearcă",
    close: "Închide",
    operationId: "ID operațiune: {id}",
    doneOk: "Operațiunea s-a încheiat cu succes.",
    doneWithErrors: "Operațiunea s-a încheiat cu erori. Unele rezervări nu au fost modificate.",
    doneWithErrorsExport: "Exportul s-a încheiat cu erori. Unele rezervări nu au fost incluse în fișier.",
    doneUnknown: "Operațiunea s-a încheiat, dar pentru unele rezervări rezultatul nu poate fi confirmat. Vedeți detaliile mai jos.",
    unknownNote: "Pentru rezervările cu rezultat necunoscut, verificați rezervarea înainte de a repeta acțiunea.",
    itemsTitle: "Rezultate pe rezervări",
    itemsEmpty: "Nu există rezervări vizibile pentru dvs. în această operațiune.",
    itemsHidden: "Rezervări din operațiune pe care nu le mai puteți vedea (nelistate): {count}.",
    itemsPage: "Pagina {page} din {pages}",
    prev: "Pagina anterioară",
    next: "Pagina următoare",
    colBooking: "Rezervare",
    colState: "Stare",
    colResult: "Detaliu",
    announceProgress: "Procesate {processed} din {total}.",
    announceDone: "Operațiunea s-a încheiat.",
  },
  counts: {
    succeeded: "Reușite",
    failed: "Eșuate",
    skipped: "Omise",
    unknown: "Necunoscute",
  },
  state: {
    queued: "În așteptare",
    running: "În lucru",
    completed: "Finalizată",
    completed_with_errors: "Finalizată cu erori",
  },
  item: {
    queued: "În așteptare",
    running: "În lucru",
    succeeded: "Reușit",
    failed: "Eșuat, nemodificată",
    skipped: "Omis, nemodificată",
    unknown: "Rezultat necunoscut",
  },
  codes: {
    updated: "Statutul a fost schimbat.",
    noop: "Avea deja acest statut.",
    transition_refused: "Această schimbare de statut nu este permisă pentru rezervare.",
    booking_unavailable: "Rezervarea nu este disponibilă.",
    cluster_or_provider_booking: "Rezervare de conexiune sau de la furnizor. Schimbați-o individual.",
    actor_unauthorized: "Nu aveți acces la această rezervare.",
    item_integrity_mismatch: "Datele rezervării nu corespund. Nu a fost procesată.",
    exported: "Inclusă în fișier.",
    outcome_unknown: "Rezultatul nu poate fi confirmat. Verificați rezervarea înainte de a repeta.",
    none: "—",
    fallback: "Detaliu tehnic: {code}",
  },
  smsCounts: {
    succeeded: "Trimise",
    failed: "Netrimise",
    skipped: "Omise",
    unknown: "Necunoscute",
  },
  smsItem: {
    queued: "În așteptare",
    running: "În lucru",
    succeeded: "Trimis",
    failed: "Netrimis",
    skipped: "Omis, netrimis",
    unknown: "Rezultat necunoscut",
  },
  smsCodes: {
    item_integrity_mismatch: "Datele rezervării nu corespund. SMS-ul nu a fost trimis.",
    booking_unavailable: "Rezervarea nu este disponibilă. SMS-ul nu a fost trimis.",
    actor_unauthorized: "Nu aveți acces la această rezervare. SMS-ul nu a fost trimis.",
    sms_notification_feature_disabled: "Notificările SMS nu sunt activate. SMS-ul nu a fost trimis.",
    sms_recipient_phone_missing: "Rezervarea nu are număr de telefon. SMS-ul nu a fost trimis.",
    sms_recipient_phone_invalid: "Numărul de telefon al rezervării nu este valid. SMS-ul nu a fost trimis.",
    sms_sms_sender_missing: "Expeditorul SMS nu este configurat. SMS-ul nu a fost trimis.",
    sms_notification_text_unavailable: "Textul SMS nu este disponibil pentru această rezervare. SMS-ul nu a fost trimis.",
    sms_group_identity_conflict: "Datele grupului de rezervări nu corespund. SMS-ul nu a fost trimis.",
    sms_sms_disabled: "Trimiterea SMS este dezactivată. SMS-ul nu a fost trimis.",
    sms_global_sms_disabled: "Trimiterea SMS este oprită pe platformă. SMS-ul nu a fost trimis.",
    sms_owner_sms_sender_missing: "Compania care deține rezervarea nu are expeditor SMS configurat. SMS-ul nu a fost trimis.",
    sms_owner_link_unavailable: "Compania care deține rezervarea nu este conectată pentru SMS. SMS-ul nu a fost trimis.",
    sms_owner_company_missing: "Compania care deține rezervarea nu a fost găsită. SMS-ul nu a fost trimis.",
    sms_owner_company_inactive: "Compania care deține rezervarea nu este activă. SMS-ul nu a fost trimis.",
    sms_canonical_booking_missing: "Rezervarea principală nu a fost găsită. SMS-ul nu a fost trimis.",
    sms_canonical_booking_unresolved: "Rezervarea principală nu a putut fi stabilită. SMS-ul nu a fost trimis.",
    sms_notification_feature_store_unavailable: "Setările de notificări nu pot fi citite acum. SMS-ul nu a fost trimis.",
    sms_unknown_notification: "Tipul notificării nu este recunoscut. SMS-ul nu a fost trimis.",
    sms_unsupported_rail: "Această notificare nu poate fi trimisă prin SMS. SMS-ul nu a fost trimis.",
    sms_approved_text_missing: "Nu există un text aprobat pentru mesaj. SMS-ul nu a fost trimis.",
    sms_approved_text_fallback_missing: "Lipsește mesajul în limba de rezervă. SMS-ul nu a fost trimis.",
    sms_refused: "Regulile de notificare nu permit acest SMS. SMS-ul nu a fost trimis.",
    sms_provider_failed: "Furnizorul SMS nu a acceptat mesajul.",
    sms_provider_accepted: "Acceptat de furnizor; livrarea nu este încă confirmată.",
    sms_delivered: "Livrat.",
    outcome_unknown: "Rezultatul nu poate fi confirmat. Nu retrimiteți automat; verificați istoricul rezervării.",
  },
  resume: {
    title: "Operațiuni în masă de urmărit",
    status: "Schimbare de statut",
    export: "Export selectat",
    sms: "Trimitere SMS",
    open: "Vezi progresul",
    openAria: "Vezi progresul operațiunii: {action}",
    openAriaId: "Vezi progresul operațiunii: {action}, ID {id}",
    showMore: "Arată toate ({count})",
    showLess: "Arată mai puține",
  },
  errors: {
    generic: "A apărut o eroare. Încercați din nou.",
    network: "Serverul nu a putut fi contactat. Verificați conexiunea și reîncercați.",
    forbidden: "Nu aveți permisiunea pentru această acțiune. Dacă accesul s-a schimbat, reautentificați-vă.",
    unauthenticated: "Sesiunea a expirat. Autentificați-vă din nou.",
    throttled: "Prea multe cereri într-un timp scurt. Așteptați puțin și reîncercați.",
    invalid_response: "Serverul a trimis un răspuns neașteptat. Reîncercați.",
    invalid_operation_id: "Operațiunea nu poate fi identificată.",
    bulk_operation_not_found: "Operațiunea nu mai este disponibilă pentru dvs.",
    idempotency_payload_mismatch: "Această cerere a fost deja folosită pentru altă acțiune. Închideți fereastra și porniți acțiunea din nou.",
    invalid_idempotency_key: "Cererea nu a putut fi identificată. Porniți acțiunea din nou.",
    invalid_action: "Acțiunea nu este disponibilă.",
    invalid_booking_ids: "Selecția de rezervări nu este validă. Selectați din nou.",
    too_many_booking_ids: "Puteți trimite cel mult 200 de rezervări odată.",
    invalid_payload: "Datele cererii nu sunt valide.",
    unexpected_field: "Datele cererii nu sunt valide.",
    invalid_target_status: "Statutul ales nu mai este disponibil. Alegeți altul.",
    invalid_reason: "Motivul nu este valid sau este prea lung.",
    invalid_refund_mode: "Modul de rambursare nu este acceptat.",
    invalid_format: "Formatul exportului nu este acceptat.",
    invalid_page: "Pagina cerută nu este validă.",
    invalid_per_page: "Pagina cerută nu este validă.",
    bulk_export_not_ready: "Fișierul nu este încă gata.",
    bulk_export_expired: "Fișierul a expirat. Creați un export nou.",
    bulk_export_unavailable: "Fișierul nu mai este disponibil. Creați un export nou.",
    bulk_export_scope_changed: "Accesul la unele rezervări s-a schimbat. Creați un export nou.",
    invalid_sms_messages: "Scrieți cel puțin un mesaj SMS valid.",
    invalid_sms_message: "Un mesaj SMS este gol sau prea lung.",
    invalid_sms_language: "Limba mesajului SMS nu este acceptată.",
    invalid_sms_fallback: "Alegeți limba de rezervă dintre limbile incluse.",
  },
};

const ru: Catalog = {
  plural: {
    bookings: {
      one: "{count} бронирование",
      few: "{count} бронирования",
      many: "{count} бронирований",
      other: "{count} бронирования",
    },
    selected: {
      one: "Выбрано: {count}",
      few: "Выбрано: {count}",
      many: "Выбрано: {count}",
      other: "Выбрано: {count}",
    },
  },
  selection: {
    rowLabel: "Выбрать бронирование #{id}",
    pageLabel: "Выбрать все бронирования на этой странице",
    limit: "За один раз можно выбрать не более {max} бронирований.",
    announce: "Выбрано бронирований: {count}.",
    cleared: "Выбор очищен.",
  },
  toolbar: {
    region: "Действия с выбранными бронированиями",
    ids: "ID: {ids}",
    idsMore: "+{count}",
    showIds: "Все выбранные ID",
    status: "Изменить статус",
    export: "Экспорт выбранных",
    clear: "Очистить выбор",
    statusAria: "Изменить статус: {bookings}",
    exportAria: "Экспорт выбранных: {bookings}, в XLSX",
    clearAria: "Очистить выбор ({bookings})",
    sms: "Отправить SMS",
    smsAria: "Отправить SMS: {bookings}",
    smsBlocked: "Предыдущая отправка SMS не подтверждена. Сначала повторите её или откажитесь от неё.",
  },
  sms: {
    title: "Отправить SMS",
    reviewTitle: "Проверьте SMS",
    heading: "SMS для: {bookings}",
    reviewHeading: "Проверьте SMS для: {bookings}",
    ids: "ID бронирований",
    intro: "Напишите сообщение на нужных языках. Каждое бронирование получает сообщение на своём языке; если сообщения на этом языке нет, отправляется сообщение на резервном языке.",
    restored: "Мы сохранили незаконченное сообщение для этих бронирований.",
    discard: "Удалить сообщение",
    discarded: "Сообщение удалено.",
    languagesLegend: "Языки сообщения",
    lang: {
      ro: "Румынский",
      ru: "Русский",
      en: "Английский",
    },
    include: {
      ro: "Добавить сообщение на румынском",
      ru: "Добавить сообщение на русском",
      en: "Добавить сообщение на английском",
    },
    messageLabel: {
      ro: "Сообщение на румынском",
      ru: "Сообщение на русском",
      en: "Сообщение на английском",
    },
    hint: "От 1 до {max} символов. Пробелы в начале и в конце не считаются.",
    counter: "{count}/{max} символов",
    errorEmpty: "Напишите сообщение или снимите отметку с этого языка.",
    errorTooLong: "В сообщении {count} символов. Максимум {max}.",
    errorNoLanguage: "Выберите хотя бы один язык и напишите сообщение.",
    fallbackLegend: "Резервный язык",
    fallbackHelp: "Используется, когда для языка бронирования нет отдельного сообщения.",
    fallbackChanged: "Резервный язык теперь: {language}.",
    fallbackCleared: "Не выбран ни один язык. Выберите хотя бы один.",
    review: "Проверить сообщение",
    back: "Вернуться к сообщению",
    cancel: "Отмена",
    confirm: "Подтвердить и отправить",
    reviewLanguages: "Выбранные языки",
    reviewFallback: "Резервный язык",
    reviewNote: "После подтверждения отправку нельзя остановить. Номер телефона и отправителя сервер определяет для каждого бронирования.",
    closeTitle: "Закрыть окно?",
    closeText: "Сообщение не отправлено. Если закрыть окно, оно останется только на этой странице, пока вы не измените выбор, фильтры, компанию или учётную запись.",
    closeStay: "Продолжить редактирование",
    closeKeep: "Закрыть и сохранить сообщение",
    closeDiscard: "Удалить сообщение и закрыть",
  },
  smsPending: {
    region: "Неподтверждённая отправка SMS",
    text: "Неизвестно, дошла ли последняя отправка SMS до сервера. Новое SMS нельзя отправить, пока вы не повторите её или не откажетесь от неё.",
    textNoRetry: "Неизвестно, дошла ли последняя отправка SMS до сервера, и повторить её отсюда уже нельзя. Проверьте историю бронирований, затем откажитесь от неё, чтобы написать новое сообщение.",
    open: "Показать попытку и повторить",
    abandon: "Отказаться от попытки",
    abandonConfirm: "Отказаться от попытки? Ничего не отправляется и не отменяется: если SMS уже дошло до сервера, оно останется отправленным. Новое сообщение потребует отдельной проверки и подтверждения.",
    abandonKeep: "Нет, сохранить попытку",
    abandonYes: "Да, отказаться",
    abandoned: "Вы отказались от попытки. Можно написать новое сообщение.",
  },
  status: {
    title: "Изменить статус выбранных бронирований",
    intro: "Новый статус применяется только к выбранным бронированиям. Те, которые нельзя изменить, будут показаны отдельно.",
    targetLabel: "Новый статус",
    targetPlaceholder: "Выберите статус",
    targetRequired: "Выберите новый статус.",
    reasonLabel: "Причина (необязательно)",
    reasonHint: "Не более {max} символов.",
    reasonTooLong: "Причина длиннее {max} символов.",
    refundNote: "Возвраты, если они есть, выполняются по политике маршрута и здесь не меняются.",
    noStatuses: "Список статусов недоступен. Перезагрузите страницу.",
    review: "Далее",
    back: "Назад",
    cancel: "Отмена",
    confirmTitle: "Подтвердите изменение",
    confirmText: "Статус «{title}» (ID {value}) будет установлен для: {bookings}.",
    confirmIds: "ID бронирований",
    confirm: "Подтвердить изменение",
    invalidated: "Выбор, учётная запись или права изменились. Изменение статуса не отправлено; выберите бронирования заново.",
  },
  export: {
    confirmTitle: "Экспорт выбранных бронирований",
    confirmText: "Будет создан файл XLSX: {bookings}. Экспорт отфильтрованного списка остаётся отдельным.",
    confirmIds: "ID бронирований",
    confirm: "Создать экспорт",
    cancel: "Отмена",
    download: "Скачать XLSX",
    downloading: "Скачивание…",
    downloaded: "Скачивание началось.",
    expiresAt: "Файл доступен до {date}.",
    partialFile: "Файл содержит только успешно экспортированные бронирования. Ниже показаны результаты по бронированиям, которые вам доступны.",
    preparing: "Файл готовится…",
    noFile: "Для этого экспорта нет файла для скачивания.",
    notReady: "Файл ещё не готов. Статус обновлён.",
    expired: "Срок действия файла истёк. Создайте новый экспорт.",
    scopeChanged: "После создания файла изменился доступ к некоторым бронированиям, поэтому его нельзя скачать. Создайте новый экспорт.",
    unavailable: "Файл больше недоступен. Создайте новый экспорт.",
    newExport: "Создать новый экспорт",
    newExportHint: "Новый экспорт использует бронирования, выбранные сейчас в списке.",
  },
  progress: {
    titleStatus: "Массовое изменение статуса",
    titleExport: "Экспорт выбранных бронирований",
    titleSms: "Отправка SMS",
    doneOkSms: "Отправка завершена. «Отправлено» значит, что поставщик принял SMS; доставка подтверждена только там, где указано «Доставлено».",
    doneWithErrorsSms: "Отправка завершена с ошибками. Некоторые SMS не отправлены; подробности ниже.",
    doneUnknownSms: "Отправка завершена, но для некоторых бронирований нельзя подтвердить результат SMS. Подробности ниже.",
    smsUnknown: "Для некоторых бронирований нельзя подтвердить, было ли отправлено SMS. Не отправляйте повторно автоматически: проверьте историю бронирования.",
    smsUncertainNote: "Закрытие окна не отменяет попытку. Пока вы не повторите её или явно не откажетесь от неё, новое SMS отправить нельзя.",
    smsRetryRefused: "Повтор отклонён, но первая отправка SMS по-прежнему не подтверждена: возможно, SMS уже отправлено. Закройте окно и проверьте историю бронирований, прежде чем отказаться от попытки или отправить новое сообщение.",
    submitting: "Отправка запроса…",
    uncertain: "Неизвестно, дошёл ли запрос до сервера. Повтор безопасен: отправляется тот же запрос, действие не выполнится дважды.",
    retrySubmit: "Повторить отправку",
    rejected: "Запрос не принят.",
    loading: "Загрузка прогресса…",
    processed: "Обработано {processed} из {total}",
    progressLabel: "Прогресс операции",
    polling: "Статус обновляется автоматически.",
    runningNote: "Окно можно закрыть. Операция продолжается на сервере, её можно снова открыть из списка бронирований.",
    retry: "Повторить",
    close: "Закрыть",
    operationId: "ID операции: {id}",
    doneOk: "Операция успешно завершена.",
    doneWithErrors: "Операция завершена с ошибками. Некоторые бронирования не изменены.",
    doneWithErrorsExport: "Экспорт завершён с ошибками. Некоторые бронирования не вошли в файл.",
    doneUnknown: "Операция завершена, но для некоторых бронирований результат нельзя подтвердить. Подробности ниже.",
    unknownNote: "Для бронирований с неизвестным результатом проверьте бронирование, прежде чем повторять действие.",
    itemsTitle: "Результаты по бронированиям",
    itemsEmpty: "В этой операции нет доступных вам бронирований.",
    itemsHidden: "Бронирования операции, которые вам больше не видны (не показаны): {count}.",
    itemsPage: "Страница {page} из {pages}",
    prev: "Предыдущая страница",
    next: "Следующая страница",
    colBooking: "Бронирование",
    colState: "Состояние",
    colResult: "Подробности",
    announceProgress: "Обработано {processed} из {total}.",
    announceDone: "Операция завершена.",
  },
  counts: {
    succeeded: "Успешно",
    failed: "Ошибки",
    skipped: "Пропущено",
    unknown: "Неизвестно",
  },
  state: {
    queued: "В очереди",
    running: "Выполняется",
    completed: "Завершена",
    completed_with_errors: "Завершена с ошибками",
  },
  item: {
    queued: "В очереди",
    running: "Выполняется",
    succeeded: "Успешно",
    failed: "Ошибка, не изменено",
    skipped: "Пропущено, не изменено",
    unknown: "Результат неизвестен",
  },
  codes: {
    updated: "Статус изменён.",
    noop: "Статус уже был таким.",
    transition_refused: "Такое изменение статуса для бронирования недопустимо.",
    booking_unavailable: "Бронирование недоступно.",
    cluster_or_provider_booking: "Бронирование пересадки или поставщика. Измените его отдельно.",
    actor_unauthorized: "У вас нет доступа к этому бронированию.",
    item_integrity_mismatch: "Данные бронирования не совпадают. Не обработано.",
    exported: "Включено в файл.",
    outcome_unknown: "Результат нельзя подтвердить. Проверьте бронирование, прежде чем повторять.",
    none: "—",
    fallback: "Технический код: {code}",
  },
  smsCounts: {
    succeeded: "Отправлено",
    failed: "Не отправлено",
    skipped: "Пропущено",
    unknown: "Неизвестно",
  },
  smsItem: {
    queued: "В очереди",
    running: "Выполняется",
    succeeded: "Отправлено",
    failed: "Не отправлено",
    skipped: "Пропущено, не отправлено",
    unknown: "Результат неизвестен",
  },
  smsCodes: {
    item_integrity_mismatch: "Данные бронирования не совпадают. SMS не отправлено.",
    booking_unavailable: "Бронирование недоступно. SMS не отправлено.",
    actor_unauthorized: "У вас нет доступа к этому бронированию. SMS не отправлено.",
    sms_notification_feature_disabled: "SMS-уведомления не включены. SMS не отправлено.",
    sms_recipient_phone_missing: "В бронировании нет номера телефона. SMS не отправлено.",
    sms_recipient_phone_invalid: "Номер телефона в бронировании недействителен. SMS не отправлено.",
    sms_sms_sender_missing: "Отправитель SMS не настроен. SMS не отправлено.",
    sms_notification_text_unavailable: "Текст SMS для этого бронирования недоступен. SMS не отправлено.",
    sms_group_identity_conflict: "Данные группы бронирований не совпадают. SMS не отправлено.",
    sms_sms_disabled: "Отправка SMS отключена. SMS не отправлено.",
    sms_global_sms_disabled: "Отправка SMS отключена на платформе. SMS не отправлено.",
    sms_owner_sms_sender_missing: "У компании-владельца бронирования не настроен отправитель SMS. SMS не отправлено.",
    sms_owner_link_unavailable: "Компания-владелец бронирования не подключена для SMS. SMS не отправлено.",
    sms_owner_company_missing: "Компания-владелец бронирования не найдена. SMS не отправлено.",
    sms_owner_company_inactive: "Компания-владелец бронирования неактивна. SMS не отправлено.",
    sms_canonical_booking_missing: "Основное бронирование не найдено. SMS не отправлено.",
    sms_canonical_booking_unresolved: "Не удалось определить основное бронирование. SMS не отправлено.",
    sms_notification_feature_store_unavailable: "Настройки уведомлений сейчас недоступны. SMS не отправлено.",
    sms_unknown_notification: "Тип уведомления не распознан. SMS не отправлено.",
    sms_unsupported_rail: "Это уведомление нельзя отправить по SMS. SMS не отправлено.",
    sms_approved_text_missing: "Нет утверждённого текста сообщения. SMS не отправлено.",
    sms_approved_text_fallback_missing: "Нет сообщения на резервном языке. SMS не отправлено.",
    sms_refused: "Правила уведомлений не разрешают это SMS. SMS не отправлено.",
    sms_provider_failed: "Поставщик SMS не принял сообщение.",
    sms_provider_accepted: "Принято поставщиком; доставка ещё не подтверждена.",
    sms_delivered: "Доставлено.",
    outcome_unknown: "Результат нельзя подтвердить. Не отправляйте повторно автоматически; проверьте историю бронирования.",
  },
  resume: {
    title: "Массовые операции для отслеживания",
    status: "Изменение статуса",
    export: "Экспорт выбранных",
    sms: "Отправка SMS",
    open: "Показать прогресс",
    openAria: "Показать прогресс операции: {action}",
    openAriaId: "Показать прогресс операции: {action}, ID {id}",
    showMore: "Показать все ({count})",
    showLess: "Показать меньше",
  },
  errors: {
    generic: "Произошла ошибка. Попробуйте ещё раз.",
    network: "Не удалось связаться с сервером. Проверьте подключение и повторите.",
    forbidden: "У вас нет прав на это действие. Если доступ изменился, войдите заново.",
    unauthenticated: "Сессия истекла. Войдите заново.",
    throttled: "Слишком много запросов за короткое время. Подождите и повторите.",
    invalid_response: "Сервер прислал неожиданный ответ. Повторите.",
    invalid_operation_id: "Не удалось определить операцию.",
    bulk_operation_not_found: "Операция вам больше недоступна.",
    idempotency_payload_mismatch: "Этот запрос уже использован для другого действия. Закройте окно и начните действие заново.",
    invalid_idempotency_key: "Не удалось определить запрос. Начните действие заново.",
    invalid_action: "Действие недоступно.",
    invalid_booking_ids: "Выбор бронирований недействителен. Выберите заново.",
    too_many_booking_ids: "За один раз можно отправить не более 200 бронирований.",
    invalid_payload: "Данные запроса недействительны.",
    unexpected_field: "Данные запроса недействительны.",
    invalid_target_status: "Выбранный статус больше недоступен. Выберите другой.",
    invalid_reason: "Причина недействительна или слишком длинная.",
    invalid_refund_mode: "Режим возврата не поддерживается.",
    invalid_format: "Формат экспорта не поддерживается.",
    invalid_page: "Запрошенная страница недействительна.",
    invalid_per_page: "Запрошенная страница недействительна.",
    bulk_export_not_ready: "Файл ещё не готов.",
    bulk_export_expired: "Срок действия файла истёк. Создайте новый экспорт.",
    bulk_export_unavailable: "Файл больше недоступен. Создайте новый экспорт.",
    bulk_export_scope_changed: "Доступ к некоторым бронированиям изменился. Создайте новый экспорт.",
    invalid_sms_messages: "Напишите хотя бы одно корректное SMS-сообщение.",
    invalid_sms_message: "SMS-сообщение пустое или слишком длинное.",
    invalid_sms_language: "Язык SMS-сообщения не поддерживается.",
    invalid_sms_fallback: "Выберите резервный язык из выбранных языков.",
  },
};

const en: Catalog = {
  plural: {
    bookings: {
      one: "{count} booking",
      few: "{count} bookings",
      many: "{count} bookings",
      other: "{count} bookings",
    },
    selected: {
      one: "{count} selected",
      few: "{count} selected",
      many: "{count} selected",
      other: "{count} selected",
    },
  },
  selection: {
    rowLabel: "Select booking #{id}",
    pageLabel: "Select all bookings on this page",
    limit: "You can select at most {max} bookings at once.",
    announce: "Selected bookings: {count}.",
    cleared: "Selection cleared.",
  },
  toolbar: {
    region: "Actions for the selected bookings",
    ids: "ID: {ids}",
    idsMore: "+{count}",
    showIds: "All selected IDs",
    status: "Change status",
    export: "Export selected",
    clear: "Clear selection",
    statusAria: "Change status for {bookings}",
    exportAria: "Export selected: {bookings}, to XLSX",
    clearAria: "Clear selection ({bookings})",
    sms: "Send SMS",
    smsAria: "Send SMS to {bookings}",
    smsBlocked: "The previous SMS send is not confirmed. Retry it or abandon it first.",
  },
  sms: {
    title: "Send SMS",
    reviewTitle: "Check the SMS",
    heading: "SMS for {bookings}",
    reviewHeading: "Check the SMS for {bookings}",
    ids: "Booking IDs",
    intro: "Write the message in the languages you need. Each booking gets the message in its own language; if there is no message in that language, it gets the message in the fallback language.",
    restored: "We kept your unfinished message for these bookings.",
    discard: "Delete message",
    discarded: "The message was deleted.",
    languagesLegend: "Message languages",
    lang: {
      ro: "Romanian",
      ru: "Russian",
      en: "English",
    },
    include: {
      ro: "Include a message in Romanian",
      ru: "Include a message in Russian",
      en: "Include a message in English",
    },
    messageLabel: {
      ro: "Message in Romanian",
      ru: "Message in Russian",
      en: "Message in English",
    },
    hint: "Between 1 and {max} characters. Spaces at the start and end are not counted.",
    counter: "{count}/{max} characters",
    errorEmpty: "Write the message or untick this language.",
    errorTooLong: "The message has {count} characters. The maximum is {max}.",
    errorNoLanguage: "Choose at least one language and write the message.",
    fallbackLegend: "Fallback language",
    fallbackHelp: "Used when the booking's language has no message of its own.",
    fallbackChanged: "The fallback language is now: {language}.",
    fallbackCleared: "No language is included any more. Choose at least one.",
    review: "Check message",
    back: "Back to message",
    cancel: "Cancel",
    confirm: "Confirm and send",
    reviewLanguages: "Included languages",
    reviewFallback: "Fallback language",
    reviewNote: "After you confirm, sending cannot be stopped. The server chooses the phone number and the sender for each booking.",
    closeTitle: "Close this window?",
    closeText: "The message has not been sent. If you close, it stays only on this page while you keep the same selection, filters, company and account.",
    closeStay: "Keep editing",
    closeKeep: "Close and keep message",
    closeDiscard: "Delete message and close",
  },
  smsPending: {
    region: "Unconfirmed SMS send",
    text: "We do not know whether the last SMS send reached the server. You cannot send a new SMS until you retry it or abandon it.",
    textNoRetry: "We do not know whether the last SMS send reached the server, and it can no longer be retried from here. Check the booking history, then abandon it to write a new message.",
    open: "View attempt and retry",
    abandon: "Abandon attempt",
    abandonConfirm: "Abandon this attempt? Nothing is sent or cancelled: if the SMS already reached the server, it stays sent. A new message will need its own check and confirmation.",
    abandonKeep: "No, keep the attempt",
    abandonYes: "Yes, abandon",
    abandoned: "The attempt was abandoned. You can write a new message.",
  },
  status: {
    title: "Change status of the selected bookings",
    intro: "The new status applies only to the selected bookings. Those that cannot be changed are reported separately.",
    targetLabel: "New status",
    targetPlaceholder: "Choose a status",
    targetRequired: "Choose the new status.",
    reasonLabel: "Reason (optional)",
    reasonHint: "Maximum {max} characters.",
    reasonTooLong: "The reason exceeds {max} characters.",
    refundNote: "Any refunds follow the route policy and cannot be changed here.",
    noStatuses: "The status list is not available. Reload the page.",
    review: "Continue",
    back: "Back",
    cancel: "Cancel",
    confirmTitle: "Confirm the change",
    confirmText: "Status “{title}” (ID {value}) will be set for {bookings}.",
    confirmIds: "Booking IDs",
    confirm: "Confirm change",
    invalidated: "The selection, account or permissions changed. The status change was not sent; select the bookings again.",
  },
  export: {
    confirmTitle: "Export the selected bookings",
    confirmText: "An XLSX file will be created with {bookings}. The filtered list export stays separate.",
    confirmIds: "Booking IDs",
    confirm: "Create export",
    cancel: "Cancel",
    download: "Download XLSX",
    downloading: "Downloading…",
    downloaded: "The download has started.",
    expiresAt: "The file is available until {date}.",
    partialFile: "The file contains only the successfully exported bookings. The per-booking results visible to you are shown below.",
    preparing: "The file is being prepared…",
    noFile: "There is no file to download for this export.",
    notReady: "The file is not ready yet. The status was refreshed.",
    expired: "The file has expired. Create a new export.",
    scopeChanged: "Access to some bookings changed after the file was created, so it can no longer be downloaded. Create a new export.",
    unavailable: "The file is no longer available. Create a new export.",
    newExport: "Create a new export",
    newExportHint: "The new export uses the bookings currently selected in the list.",
  },
  progress: {
    titleStatus: "Bulk status change",
    titleExport: "Selected bookings export",
    titleSms: "SMS send",
    doneOkSms: "Sending has finished. “Sent” means the provider accepted the SMS; delivery is confirmed only where it says “Delivered”.",
    doneWithErrorsSms: "Sending finished with errors. Some SMS were not sent; see the details below.",
    doneUnknownSms: "Sending has finished, but the SMS result cannot be confirmed for some bookings. See the details below.",
    smsUnknown: "For some bookings we cannot confirm whether the SMS was sent. Do not resend automatically: check the booking history.",
    smsUncertainNote: "Closing this window does not abandon the attempt. Until you retry it or explicitly abandon it, you cannot send a new SMS.",
    smsRetryRefused: "The retry was refused, but the first SMS send is still unconfirmed: it may already have been sent. Close this window and check the booking history before you abandon the attempt or send a new message.",
    submitting: "Sending the request…",
    uncertain: "We cannot tell whether the request reached the server. Retrying is safe: it sends the same request and never repeats the action.",
    retrySubmit: "Retry sending",
    rejected: "The request was not accepted.",
    loading: "Loading progress…",
    processed: "Processed {processed} of {total}",
    progressLabel: "Operation progress",
    polling: "The status updates automatically.",
    runningNote: "You can close this window. The operation continues on the server and you can reopen it from the booking list.",
    retry: "Retry",
    close: "Close",
    operationId: "Operation ID: {id}",
    doneOk: "The operation completed successfully.",
    doneWithErrors: "The operation completed with errors. Some bookings were not changed.",
    doneWithErrorsExport: "The export completed with errors. Some bookings were not included in the file.",
    doneUnknown: "The operation has finished, but the result cannot be confirmed for some bookings. See the details below.",
    unknownNote: "For bookings with an unknown result, check the booking before repeating the action.",
    itemsTitle: "Results per booking",
    itemsEmpty: "This operation has no bookings visible to you.",
    itemsHidden: "Bookings in this operation you can no longer see (not listed): {count}.",
    itemsPage: "Page {page} of {pages}",
    prev: "Previous page",
    next: "Next page",
    colBooking: "Booking",
    colState: "State",
    colResult: "Detail",
    announceProgress: "Processed {processed} of {total}.",
    announceDone: "The operation has finished.",
  },
  counts: {
    succeeded: "Succeeded",
    failed: "Failed",
    skipped: "Skipped",
    unknown: "Unknown",
  },
  state: {
    queued: "Queued",
    running: "Running",
    completed: "Completed",
    completed_with_errors: "Completed with errors",
  },
  item: {
    queued: "Queued",
    running: "Running",
    succeeded: "Succeeded",
    failed: "Failed, not changed",
    skipped: "Skipped, not changed",
    unknown: "Unknown result",
  },
  codes: {
    updated: "The status was changed.",
    noop: "It already had this status.",
    transition_refused: "This status change is not allowed for the booking.",
    booking_unavailable: "The booking is not available.",
    cluster_or_provider_booking: "Connection or provider booking. Change it individually.",
    actor_unauthorized: "You do not have access to this booking.",
    item_integrity_mismatch: "The booking data does not match. It was not processed.",
    exported: "Included in the file.",
    outcome_unknown: "The result cannot be confirmed. Check the booking before repeating.",
    none: "—",
    fallback: "Technical code: {code}",
  },
  smsCounts: {
    succeeded: "Sent",
    failed: "Not sent",
    skipped: "Skipped",
    unknown: "Unknown",
  },
  smsItem: {
    queued: "Queued",
    running: "Running",
    succeeded: "Sent",
    failed: "Not sent",
    skipped: "Skipped, not sent",
    unknown: "Unknown result",
  },
  smsCodes: {
    item_integrity_mismatch: "The booking data does not match. The SMS was not sent.",
    booking_unavailable: "The booking is not available. The SMS was not sent.",
    actor_unauthorized: "You do not have access to this booking. The SMS was not sent.",
    sms_notification_feature_disabled: "SMS notifications are not enabled. The SMS was not sent.",
    sms_recipient_phone_missing: "The booking has no phone number. The SMS was not sent.",
    sms_recipient_phone_invalid: "The booking's phone number is not valid. The SMS was not sent.",
    sms_sms_sender_missing: "The SMS sender is not set up. The SMS was not sent.",
    sms_notification_text_unavailable: "The SMS text is not available for this booking. The SMS was not sent.",
    sms_group_identity_conflict: "The booking group data does not match. The SMS was not sent.",
    sms_sms_disabled: "SMS sending is turned off. The SMS was not sent.",
    sms_global_sms_disabled: "SMS sending is turned off on the platform. The SMS was not sent.",
    sms_owner_sms_sender_missing: "The company that owns the booking has no SMS sender set up. The SMS was not sent.",
    sms_owner_link_unavailable: "The company that owns the booking is not connected for SMS. The SMS was not sent.",
    sms_owner_company_missing: "The company that owns the booking was not found. The SMS was not sent.",
    sms_owner_company_inactive: "The company that owns the booking is not active. The SMS was not sent.",
    sms_canonical_booking_missing: "The main booking was not found. The SMS was not sent.",
    sms_canonical_booking_unresolved: "The main booking could not be determined. The SMS was not sent.",
    sms_notification_feature_store_unavailable: "The notification settings cannot be read right now. The SMS was not sent.",
    sms_unknown_notification: "The notification type is not recognised. The SMS was not sent.",
    sms_unsupported_rail: "This notification cannot be sent by SMS. The SMS was not sent.",
    sms_approved_text_missing: "There is no approved message text. The SMS was not sent.",
    sms_approved_text_fallback_missing: "The message in the fallback language is missing. The SMS was not sent.",
    sms_refused: "The notification rules do not allow this SMS. The SMS was not sent.",
    sms_provider_failed: "The SMS provider did not accept the message.",
    sms_provider_accepted: "Accepted by the provider; delivery is not confirmed yet.",
    sms_delivered: "Delivered.",
    outcome_unknown: "The result cannot be confirmed. Do not resend automatically; check the booking history.",
  },
  resume: {
    title: "Bulk operations to follow",
    status: "Status change",
    export: "Selected export",
    sms: "SMS send",
    open: "View progress",
    openAria: "View progress of the operation: {action}",
    openAriaId: "View progress of the operation: {action}, ID {id}",
    showMore: "Show all ({count})",
    showLess: "Show fewer",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "The server could not be reached. Check your connection and retry.",
    forbidden: "You do not have permission for this action. If your access changed, sign in again.",
    unauthenticated: "Your session has expired. Please sign in again.",
    throttled: "Too many requests in a short time. Wait a moment and retry.",
    invalid_response: "The server sent an unexpected response. Please retry.",
    invalid_operation_id: "The operation cannot be identified.",
    bulk_operation_not_found: "The operation is no longer available to you.",
    idempotency_payload_mismatch: "This request was already used for another action. Close the window and start the action again.",
    invalid_idempotency_key: "The request could not be identified. Start the action again.",
    invalid_action: "The action is not available.",
    invalid_booking_ids: "The booking selection is not valid. Select again.",
    too_many_booking_ids: "You can send at most 200 bookings at once.",
    invalid_payload: "The request data is not valid.",
    unexpected_field: "The request data is not valid.",
    invalid_target_status: "The chosen status is no longer available. Choose another one.",
    invalid_reason: "The reason is not valid or is too long.",
    invalid_refund_mode: "The refund mode is not supported.",
    invalid_format: "The export format is not supported.",
    invalid_page: "The requested page is not valid.",
    invalid_per_page: "The requested page is not valid.",
    bulk_export_not_ready: "The file is not ready yet.",
    bulk_export_expired: "The file has expired. Create a new export.",
    bulk_export_unavailable: "The file is no longer available. Create a new export.",
    bulk_export_scope_changed: "Access to some bookings changed. Create a new export.",
    invalid_sms_messages: "Write at least one valid SMS message.",
    invalid_sms_message: "An SMS message is empty or too long.",
    invalid_sms_language: "The SMS message language is not supported.",
    invalid_sms_fallback: "Choose the fallback language from the included languages.",
  },
};

export const I7B_MESSAGES: Readonly<Record<I7bLocale, Catalog>> = {ro, ru, en};

export const I7B_PLURAL_FORMS = ["one", "few", "many", "other"] as const;

export function resolveI7bLocale(value: unknown): I7bLocale {
  return (I7B_LOCALES as readonly unknown[]).includes(value) ? (value as I7bLocale) : I7B_DEFAULT_LOCALE;
}

// Private instance holding only the I7-B catalog; the application catalog is untouched.
const catalog = createI18n({
  legacy: false,
  locale: I7B_DEFAULT_LOCALE,
  fallbackLocale: I7B_DEFAULT_LOCALE,
  missingWarn: false,
  fallbackWarn: false,
  messages: {
    ro: {i7b: I7B_MESSAGES.ro},
    ru: {i7b: I7B_MESSAGES.ru},
    en: {i7b: I7B_MESSAGES.en},
  },
});

// The application instance registers itself from `core/plugins/i18n.ts`, so
// I7-B follows its locale. Without it (isolated tests) the locale is `ro`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GlobalComposer = any;
let registered: {global: GlobalComposer} | null = null;

export function registerI7bI18n(instance: {global: GlobalComposer}): void {
  registered = instance;
}

export interface I7bI18n {
  t: (key: string, named?: Record<string, unknown>) => string;
  tError: (code: string | null | undefined) => string;
  tCode: (code: string | null | undefined) => string;
  tSmsCode: (code: string | null | undefined) => string;
  plural: (key: string, count: number) => string;
  formatDate: (value: string | null) => string;
  locale: ComputedRef<I7bLocale>;
  elementLocale: ComputedRef<ReturnType<typeof elementLocaleFor>>;
}

export function useI7b(): I7bI18n {
  const global = catalog.global;
  const locale = computed(() => resolveI7bLocale(registered ? unref(registered.global.locale) : I7B_DEFAULT_LOCALE));
  const elementLocale = computed(() => elementLocaleFor(locale.value));
  const has = (key: string) => global.te(`i7b.${key}`, locale.value);
  const t = (key: string, named: Record<string, unknown> = {}): string =>
    global.t(`i7b.${key}`, named, {locale: locale.value});
  // Stable backend/local codes map to the bounded catalog; anything unknown
  // falls back to generic copy, so server prose is never rendered.
  const tError = (code: string | null | undefined): string =>
    code && has(`errors.${code}`) ? t(`errors.${code}`) : t("errors.generic");
  const tCode = (code: string | null | undefined): string => {
    if (!code) return t("codes.none");
    return has(`codes.${code}`) && code !== "none" && code !== "fallback"
      ? t(`codes.${code}`)
      : t("codes.fallback", {code});
  };
  // SMS items read only the SMS reason codes proven by the backend; any other
  // code keeps the technical fallback instead of borrowing a status meaning.
  const tSmsCode = (code: string | null | undefined): string => {
    if (!code) return t("codes.none");
    return has(`smsCodes.${code}`) ? t(`smsCodes.${code}`) : t("codes.fallback", {code});
  };
  const plural = (key: string, count: number): string => {
    let form = "other";
    try {
      form = new Intl.PluralRules(locale.value).select(count);
    } catch (_) {
      form = count === 1 ? "one" : "other";
    }
    const path = has(`plural.${key}.${form}`) ? `plural.${key}.${form}` : `plural.${key}.other`;
    return t(path, {count});
  };
  const formatDate = (value: string | null): string => {
    if (!value) return "—";
    const time = Date.parse(value);
    if (!Number.isFinite(time)) return value;
    try {
      return new Intl.DateTimeFormat(locale.value, {dateStyle: "medium", timeStyle: "short"}).format(new Date(time));
    } catch (_) {
      return value;
    }
  };
  return {t, tError, tCode, tSmsCode, plural, formatDate, locale, elementLocale};
}
